/**
 * CreditsCard — compact status widget for the Dashboard.
 * Progress bar, alerts, "Voir les crédits" link.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { useCredits, formatResetDate } from '@/lib/useCredits';
import { useT } from '@/lib/i18n';

const CreditsCard = () => {
  const { summary, loading } = useCredits();
  const { lang } = useT();

  if (loading || !summary) {
    return (
      <div
        className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 min-h-[168px] animate-pulse"
        data-testid="dashboard-credits-card"
      />
    );
  }

  const used = summary.used_this_period;
  const cap = summary.monthly_capacity;
  const pct = summary.pct_remaining;
  const barPct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const alert = summary.alert_level;

  const alertText = alert === 'critical'
    ? (lang === 'fr' ? 'Vous approchez de votre limite de crédits.' : 'You are close to your credit limit.')
    : alert === 'low'
    ? (lang === 'fr' ? `Il vous reste ${summary.monthly_credits} crédits.` : `${summary.monthly_credits} credits remaining.`)
    : null;

  const alertColor = alert === 'critical' ? 'text-red-400 border-red-500/20 bg-red-500/5'
    : alert === 'low' ? 'text-amber-400 border-amber-500/20 bg-amber-500/5'
    : '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="rounded-2xl border border-white/5 bg-white/[0.02] p-5"
      data-testid="dashboard-credits-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-md bg-[#D4FF00]/10 flex items-center justify-center">
            <Zap className="w-3.5 h-3.5 text-[#D4FF00]" strokeWidth={2.4} />
          </span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            {lang === 'fr' ? 'Crédits' : 'Credits'}
          </span>
        </div>
        <Link
          to="/credits"
          className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#D4FF00] font-mono flex items-center gap-1"
          data-testid="dashboard-credits-link"
        >
          {lang === 'fr' ? 'Détails' : 'Details'} <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-heading text-4xl font-black tabular-nums" data-testid="credits-monthly-remaining">
          {summary.monthly_credits}
        </span>
        <span className="text-zinc-500 text-sm">/ {cap}</span>
      </div>
      <div className="text-xs text-zinc-500 mb-3">
        {summary.purchased_credits > 0 && (
          <>+ <span className="text-[#D4FF00]" data-testid="credits-purchased">{summary.purchased_credits}</span> {lang === 'fr' ? 'achetés' : 'purchased'} · </>
        )}
        {lang === 'fr' ? 'Renouvellement' : 'Renews'} {formatResetDate(summary.period_end, lang)}
      </div>

      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${barPct}%` }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className={`h-full rounded-full ${pct <= 10 ? 'bg-red-400' : pct <= 20 ? 'bg-amber-400' : 'bg-[#D4FF00]'}`}
          data-testid="credits-progress"
        />
      </div>

      {alertText && (
        <div
          className={`mt-3 flex items-center gap-2 text-xs px-2.5 py-2 rounded-lg border ${alertColor}`}
          data-testid={`credits-alert-${alert}`}
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          <span>{alertText}</span>
        </div>
      )}
    </motion.div>
  );
};

export default CreditsCard;
